/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { CONSENT_SECTION_TYPES } from 'src/pages/superAdmin/ConsentV2/consentConstants';
import ConsentSectionWrapper from './ConsentSectionWrapper';
import ConsentFieldRenderer from './ConsentFieldRenderer';
import ConsentBrandHeader from './ConsentBrandHeader';
import ConsentPatientInfo from './ConsentPatientInfo';
import ConsentDeclaration from './ConsentDeclaration';

// ---- Conditional logic evaluation ----

function getNestedValue(obj, path) {
    if (!obj || !path) return null;
    return path.split('.').reduce((acc, key) => {
        if (acc === null || acc === undefined) return null;
        if (Array.isArray(acc)) return acc[0]?.[key];
        return acc[key];
    }, obj);
}

function getValueForCondition(field, fieldValues, leadData) {
    // Check lead data first (treatment_name, treatment_category, etc.)
    if (leadData) {
        if (field === 'treatment_name') {
            return getNestedValue(leadData, 'lead_treatments.treatment_item.name');
        }
        if (field === 'treatment_category') {
            return getNestedValue(leadData, 'lead_treatments.treatment_item.treatment_category');
        }
        if (field === 'referral') {
            return leadData.referral || getNestedValue(leadData, 'lead_treatments.referral') || null;
        }
        if (field === 'lead_tag') {
            // Tags are usually an array of objects — return comma-separated names
            const tags = leadData.tags || [];
            if (Array.isArray(tags)) {
                return tags.map((t) => (typeof t === 'string' ? t : t.name || '')).join(',');
            }
            return String(tags);
        }
        if (field === 'assignee') {
            // Assignees may be an array of user objects
            const assignees = leadData.assignees || [];
            if (Array.isArray(assignees)) {
                return assignees.map((a) =>
                    typeof a === 'string' ? a : `${a.first_name || ''} ${a.last_name || ''}`.trim()
                ).join(',');
            }
            return String(assignees);
        }
        // Form field reference (field:uuid) — strip prefix and look in fieldValues
        if (field.startsWith('field:')) {
            const fieldId = field.replace('field:', '');
            return fieldValues[fieldId] ?? null;
        }
        const leadVal = getNestedValue(leadData, field);
        if (leadVal !== null && leadVal !== undefined) return leadVal;
    }
    // Fallback to form field values
    if (field.startsWith('field:')) {
        const fieldId = field.replace('field:', '');
        return fieldValues[fieldId] ?? null;
    }
    return fieldValues[field] ?? null;
}

function evaluateCondition(actual, operator, expected) {
    // Handle null/empty cases for is_empty/is_not_empty first
    if (operator === 'is_empty') {
        return actual === null || actual === undefined || actual === '';
    }
    if (operator === 'is_not_empty') {
        return actual !== null && actual !== undefined && actual !== '';
    }

    if (actual === null || actual === undefined) {
        // null doesn't equal anything, so negation operators are true
        return operator === 'not_equals' || operator === 'not_contains' || operator === 'not_in';
    }

    const actualStr = String(actual).toLowerCase();
    const expectedStr = expected ? String(expected).toLowerCase() : '';

    switch (operator) {
        case 'equals':
            return actualStr === expectedStr;
        case 'not_equals':
            return actualStr !== expectedStr;
        case 'contains':
            return actualStr.includes(expectedStr);
        case 'not_contains':
            return !actualStr.includes(expectedStr);
        case 'starts_with':
            return actualStr.startsWith(expectedStr);
        case 'in': {
            const vals = Array.isArray(expected)
                ? expected.map((v) => String(v).toLowerCase())
                : expectedStr.split(',').map((v) => v.trim().toLowerCase());
            return vals.includes(actualStr);
        }
        case 'not_in': {
            const vals = Array.isArray(expected)
                ? expected.map((v) => String(v).toLowerCase())
                : expectedStr.split(',').map((v) => v.trim().toLowerCase());
            return !vals.includes(actualStr);
        }
        default:
            return true; // Unknown operator — don't hide
    }
}

function evaluateConditions(conditions, logic, fieldValues, leadData) {
    if (!conditions || conditions.length === 0) return true;

    const results = conditions.map((cond) => {
        const actual = getValueForCondition(cond.field, fieldValues, leadData);
        return evaluateCondition(actual, cond.operator, cond.value);
    });

    return logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
}

export function isSectionVisible(section, conditionalLogics, fieldValues, leadData) {
    // --- Inline section.condition (ConsentV2 builder format) ---
    const inlineCond = section.condition;
    if (inlineCond && inlineCond.enabled && inlineCond.conditions?.length > 0) {
        const result = evaluateConditions(inlineCond.conditions, inlineCond.logic, fieldValues, leadData);
        const visible = inlineCond.action === 'hide' ? !result : result;
        if (!visible) return false;
    }

    // --- Legacy: External conditionalLogics array (backward compat) ---
    if (!conditionalLogics || conditionalLogics.length === 0) return true;

    const sectionConditions = conditionalLogics.filter(
        (cl) => cl.type === 'section' && cl.section_id === section.id
    );
    if (sectionConditions.length === 0) return true;

    return sectionConditions.every((cond) => {
        const result = evaluateConditions(cond.conditions, cond.logic, fieldValues, leadData);
        return cond.action === 'hide' ? !result : result;
    });
}

export function isFieldVisible(field, conditionalLogics, fieldValues, leadData) {
    // Retain retired acknowledgement IDs for historical links without rendering
    // an empty grid item. Any authored content keeps the field visible.
    const meta = field.metadata || {};
    if (meta.retired_acknowledgement_placeholder === true
        && normalizeField(field).type === 'html'
        && [field.label, field.long_text, field.default_value, field.helper_text,
            meta.content, meta.helper_text, meta.display_label, meta.default_value]
            .every(value => value == null || (typeof value === 'string' && value.trim() === ''))) {
        return false;
    }

    // --- Inline field condition object (ConsentV2 builder format) ---
    // field.conditional_logic might be an object (inline) or a string ID (legacy)
    const inlineCond = field.conditional_logic;
    if (inlineCond && typeof inlineCond === 'object' && inlineCond.enabled && inlineCond.conditions?.length > 0) {
        const result = evaluateConditions(inlineCond.conditions, inlineCond.logic, fieldValues, leadData);
        const visible = inlineCond.action === 'hide' ? !result : result;
        if (!visible) return false;
    }

    // --- Legacy: ID-based lookup in conditionalLogics array ---
    if (!inlineCond || typeof inlineCond !== 'string' || !conditionalLogics) return true;
    const fieldCondition = conditionalLogics.find((cl) => cl.id === inlineCond);
    if (!fieldCondition) return true;

    const result = evaluateConditions(fieldCondition.conditions, fieldCondition.logic, fieldValues, leadData);
    return fieldCondition.action === 'hide' ? !result : result;
}

// ---- Form validation ----

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Resolve a human-readable label for validation error messages
function resolveLabel(field) {
    return field.label || field.metadata?.display_label || field.name || field.placeholder || field.metadata?.placeholder || 'This field';
}

export function validateForm(sections, fieldValues, conditionalLogics, leadData) {
    const errors = {};

    sections.forEach((section) => {
        if (!isSectionVisible(section, conditionalLogics, fieldValues, leadData)) return;

        (section.fields || []).forEach((rawField) => {
            const field = normalizeField(rawField);
            if (!isFieldVisible(field, conditionalLogics, fieldValues, leadData)) return;

            const val = fieldValues[field.id];
            const fieldType = field.type;
            // Defensive: read both `required` and `is_required` (backend
            // serializer emits the latter). normalizeField also handles
            // this, but reading both here makes the contract robust to
            // future schema variations.
            const isRequired = !!(field.required ?? field.is_required);
            const validations = field.metadata?.validations || {};
            const customError = validations.custom_error;

            if (isRequired) {
                const fieldLabel = resolveLabel(field);
                if (fieldType === 'checkbox' && val !== true) {
                    errors[field.id] = customError || `${fieldLabel} must be checked`;
                } else if (fieldType === 'checkbox_group' && (!val || val.length === 0)) {
                    errors[field.id] = customError || `${fieldLabel} is required`;
                } else if (fieldType === 'signature' && !val) {
                    errors[field.id] = customError || 'Signature is required';
                } else if (fieldType === 'toggle_ack' && val !== true) {
                    errors[field.id] = customError || 'Please acknowledge this item';
                } else if (fieldType === 'toggle_detail' && !val?.agreed) {
                    errors[field.id] = customError || `${fieldLabel} must be acknowledged`;
                } else if (fieldType === 'toggle_text' && !val?.enabled) {
                    errors[field.id] = customError || `${fieldLabel} must be acknowledged`;
                } else if (!['checkbox', 'checkbox_group', 'signature', 'toggle_ack', 'toggle_detail', 'toggle_text'].includes(fieldType)) {
                    if (!val && val !== 0) {
                        errors[field.id] = customError || `${fieldLabel} is required`;
                    }
                }
            }

            // Skip further validations if field is empty (and not required — required is already caught above)
            // For object-valued fields (toggle_detail, toggle_text) skip format checks entirely
            if (fieldType === 'toggle_detail' || fieldType === 'toggle_text') return;
            if (!val && val !== 0) return;

            if (fieldType === 'email' && val && !EMAIL_REGEX.test(val)) {
                errors[field.id] = customError || 'Please enter a valid email address';
            }

            // Pattern validation (presets + custom regex)
            if (validations.pattern?.value && typeof val === 'string' && val) {
                try {
                    const patternRegex = new RegExp(validations.pattern.value, validations.pattern.type === 'uk_postcode' ? 'i' : '');
                    if (!patternRegex.test(val)) {
                        errors[field.id] = validations.pattern.message || 'Invalid format';
                    }
                } catch (e) {
                    // Invalid regex pattern — skip validation silently
                }
            }

            // Match field validation (e.g. confirm email must match email)
            if (validations.match_field?.field_id && typeof val === 'string') {
                // Try both string and number key lookups — field IDs may be stored as either type
                const matchId = validations.match_field.field_id;
                const targetVal = fieldValues[matchId] ?? fieldValues[Number(matchId)] ?? fieldValues[String(matchId)];
                if (targetVal !== undefined && val !== targetVal) {
                    errors[field.id] = validations.match_field.message || 'Values do not match';
                }
            }

            // Min length
            if (validations.min_length?.value && typeof val === 'string' && val.length < validations.min_length.value) {
                errors[field.id] = validations.min_length.message || `Must be at least ${validations.min_length.value} characters`;
            }

            // Max length
            if (validations.max_length?.value && typeof val === 'string' && val.length > validations.max_length.value) {
                errors[field.id] = validations.max_length.message || `Cannot exceed ${validations.max_length.value} characters`;
            }

            // Number range validation
            if (fieldType === 'number') {
                const numVal = Number(val);
                if (!Number.isNaN(numVal)) {
                    const minVal = validations.min_value?.value;
                    const maxVal = validations.max_value?.value;
                    if (minVal !== undefined && numVal < Number(minVal)) {
                        errors[field.id] = validations.min_value?.message || `Must be at least ${minVal}`;
                    } else if (maxVal !== undefined && numVal > Number(maxVal)) {
                        errors[field.id] = validations.max_value?.message || `Cannot exceed ${maxVal}`;
                    }
                }
            }
        });
    });

    return errors;
}

/**
 * Validate a single field (for real-time / on-blur validation).
 * Only runs FORMAT validations (email, pattern, min/max length, match_field).
 * Does NOT check `required` — that only fires on submit.
 * Returns error string or null.
 */
export function validateFieldRealtime(field, value, fieldValues) {
    const normalizedField = normalizeField(field);
    const { type: fieldType, metadata } = normalizedField;
    const validations = metadata?.validations || {};
    const customError = validations.custom_error;

    // If empty, no real-time error (required is submit-only)
    if (!value && value !== 0) return null;

    // Email format
    if (fieldType === 'email' && value && !EMAIL_REGEX.test(value)) {
        return customError || 'Please enter a valid email address';
    }

    // Pattern (regex + presets like uk_postcode)
    if (validations.pattern?.value && typeof value === 'string' && value) {
        try {
            const patternRegex = new RegExp(
                validations.pattern.value,
                validations.pattern.type === 'uk_postcode' ? 'i' : ''
            );
            if (!patternRegex.test(value)) {
                return validations.pattern.message || 'Invalid format';
            }
        } catch {
            // Invalid regex — skip
        }
    }

    // Match field (e.g. confirm email)
    if (validations.match_field?.field_id && typeof value === 'string') {
        const matchId = validations.match_field.field_id;
        const targetVal = fieldValues[matchId] ?? fieldValues[Number(matchId)] ?? fieldValues[String(matchId)];
        if (targetVal !== undefined && value !== targetVal) {
            return validations.match_field.message || 'Values do not match';
        }
    }

    // Min length
    if (validations.min_length?.value && typeof value === 'string' && value.length < validations.min_length.value) {
        return validations.min_length.message || `Must be at least ${validations.min_length.value} characters`;
    }

    // Max length
    if (validations.max_length?.value && typeof value === 'string' && value.length > validations.max_length.value) {
        return validations.max_length.message || `Cannot exceed ${validations.max_length.value} characters`;
    }

    // Number range
    if (fieldType === 'number') {
        const numVal = Number(value);
        if (!Number.isNaN(numVal)) {
            const minVal = validations.min_value?.value;
            const maxVal = validations.max_value?.value;
            if (minVal !== undefined && numVal < Number(minVal)) {
                return validations.min_value?.message || `Must be at least ${minVal}`;
            }
            if (maxVal !== undefined && numVal > Number(maxVal)) {
                return validations.max_value?.message || `Cannot exceed ${maxVal}`;
            }
        }
    }

    return null;
}

// ---- Field width (12-column flexbox grid, matching builder) ----
//
// Returns a responsive flex shorthand: collapse to a single column on mobile
// (xs, < 600px) and honour the authored columnSpan from sm and up. At native
// mobile widths a 2/5/5 Title|First|Last split is unreadable, so we mirror the
// same collapse FormQuickPreviewDrawer applies in its mobile preview frame.

function getFieldWidth(field) {
    const span = field.columnSpan || field.metadata?.style?.columnSpan;
    let desktopWidth;
    if (span) {
        if (span >= 12) {
            desktopWidth = '100%';
        } else {
            const percentage = (span / 12) * 100;
            desktopWidth = `calc(${percentage}% - ${(1 - span / 12) * 16}px)`;
        }
    } else {
        desktopWidth = '100%';
    }
    return { xs: '0 0 100%', sm: `0 0 ${desktopWidth}` };
}

// ---- Helpers ----

// Resolve section type — may be set directly (transformed data) or in description JSON (raw API)
function resolveSectionType(section) {
    if (section.section_type) return section.section_type;
    // Try to parse from description JSON
    if (section.description && typeof section.description === 'string') {
        try {
            const parsed = JSON.parse(section.description);
            return parsed.frontend_type || 'custom';
        } catch {
            // Not JSON — ignore
        }
    }
    return 'custom';
}

// Resolve field type — may be set directly (transformed) or in metadata (raw API)
function resolveFieldType(field) {
    if (field.type && field.type !== 'char' && field.type !== 'Single Line Text') return field.type;
    return field.metadata?.frontend_type || 'text';
}

// Normalize a raw API field into the shape ConsentFieldRenderer expects
export function normalizeField(field) {
    // Already transformed — has a valid type. Even on this early-return
    // path we MUST ensure `required` is normalized: the backend serializer
    // emits `is_required`, and any consumer (e.g. validateForm) reading
    // `field.required` would otherwise see undefined and silently skip the
    // requirement check (this was the root cause of signature fields being
    // bypassed on the last step of advanced forms).
    if (field.type && field.type !== 'char' && field.type !== 'Single Line Text') {
        if (field.required === undefined && (field.is_required !== undefined)) {
            return { ...field, required: !!field.is_required };
        }
        return field;
    }

    const meta = field.metadata || {};
    const frontendType = meta.frontend_type || 'text';

    let options = null;
    if (meta.options_full && Array.isArray(meta.options_full)) {
        options = meta.options_full;
    } else if (field.choices && Array.isArray(field.choices) && field.choices.length > 0) {
        options = field.choices.map((c) => (typeof c === 'string' ? { label: c, value: c } : c));
    }

    const isHtmlField = frontendType === 'html';

    let defaultValue = null;
    if (isHtmlField) {
        // HTML content: prefer long_text (new), fallback to label (old forms pre-long_text)
        defaultValue = field.long_text || field.label || null;
    } else if (frontendType === 'paragraph_display' && meta.content) {
        defaultValue = meta.content;
    } else if (meta.image_url) {
        defaultValue = meta.image_url;
    }

    return {
        ...field,
        type: frontendType,
        // For HTML fields: display label from metadata, not the content
        label: isHtmlField ? (meta.display_label || '') : field.label,
        placeholder: meta.placeholder || field.placeholder || '',
        required: field.required ?? field.is_required ?? false,
        lead_mapping: meta.lead_mapping || field.lead_mapping || null,
        default_value: defaultValue ?? field.default_value ?? null,
        helper_text: meta.helper_text || field.helper_text || '',
        options: options || field.options || null,
        metadata: meta,
    };
}

// ---- Renderer ----

const ConsentFormRenderer = ({ sections, fieldValues, onChange, errors, conditionalLogics, leadData, brandInfo, readOnly, theme, onFieldBlur, sectionIds, skipBrandSection, forPdf }) => {
    // Defensive: callers occasionally hand us `undefined` (e.g. when the
    // form schema fetch is in flight or returns an unexpected shape). The
    // public flows always pass a real array, but the dashboard
    // SubmissionDetailDialog originally passed `form=` instead of
    // `sections=`, which white-screened the page on
    // ``[...sections]`` (TypeError: sections is not iterable). Treat a
    // missing/non-array prop as "render nothing yet" rather than crashing.
    const sectionsList = Array.isArray(sections) ? sections : [];
    const sortedSections = [...sectionsList].sort((a, b) => a.order - b.order);
    // If sectionIds provided, only render those sections (for step-wise / bundle rendering)
    // Use string comparison to handle mixed types (integer API IDs vs UUID builder IDs)
    const filteredSections = sectionIds
        ? sortedSections.filter((s) => sectionIds.some((sid) => String(sid) === String(s.id)))
        : sortedSections;
    const visibleSections = filteredSections.filter(
        (s) => isSectionVisible(s, conditionalLogics, fieldValues, leadData)
    );

    const renderSectionDivider = (idx) => {
        if (!theme?.showSectionDividers || idx >= visibleSections.length - 1) return null;
        return (
            <Box
                sx={{
                    borderTop: `${theme?.sectionDividerWidth || 1}px ${theme?.sectionDividerStyle || 'solid'} ${theme?.sectionDividerColor || '#e0e0e0'}`,
                    my: `${theme?.sectionDividerSpacing || 16}px`,
                    mx: 'auto',
                    width: '100%',
                }}
            />
        );
    };

    return (
        <Box>
            {visibleSections.map((section, index) => {

                const sortedFields = [...(section.fields || [])].sort((a, b) => a.order - b.order);
                // Normalize fields to ensure they have top-level type, placeholder, etc.
                const normalizedFields = sortedFields.map(normalizeField);
                const visibleFields = normalizedFields.filter(
                    (f) => isFieldVisible(f, conditionalLogics, fieldValues, leadData)
                );

                const sectionType = resolveSectionType(section);

                // Brand image section — skip if parent already renders it (e.g. AdvancedFormFlow)
                if (sectionType === CONSENT_SECTION_TYPES.BRAND_IMAGE && skipBrandSection) {
                    return null;
                }
                if (sectionType === CONSENT_SECTION_TYPES.BRAND_IMAGE) {
                    const imageField = visibleFields.find((f) => resolveFieldType(f) === 'image');
                    const logoUrl = imageField?.default_value
                        ? imageField.default_value.replace('media:', '')
                        : brandInfo?.logoUrl;
                    return (
                        <Box key={section.id}>
                            <ConsentBrandHeader
                                logoUrl={logoUrl || ''}
                                practiceName={brandInfo?.practiceName || ''}
                                primaryColor={brandInfo?.primaryColor}
                                theme={theme}
                            />
                            {renderSectionDivider(index)}
                        </Box>
                    );
                }

                // Patient info section — grid layout
                if (sectionType === CONSENT_SECTION_TYPES.PATIENT_INFO) {
                    return (
                        <Box key={section.id} data-pdf-block="section">
                            <ConsentSectionWrapper section={section} theme={theme}>
                                <ConsentPatientInfo
                                    fields={visibleFields}
                                    fieldValues={fieldValues}
                                    onChange={onChange}
                                    errors={errors}
                                    leadData={leadData}
                                    readOnly={readOnly}
                                    theme={theme}
                                />
                            </ConsentSectionWrapper>
                            {renderSectionDivider(index)}
                        </Box>
                    );
                }

                // Declaration section
                if (sectionType === CONSENT_SECTION_TYPES.DECLARATION) {
                    return (
                        <Box key={section.id} data-pdf-block="section">
                            <ConsentSectionWrapper section={section} theme={theme}>
                                <ConsentDeclaration
                                    fields={visibleFields}
                                    fieldValues={fieldValues}
                                    onChange={onChange}
                                    errors={errors}
                                    readOnly={readOnly}
                                    theme={theme}
                                    forPdf={forPdf}
                                />
                            </ConsentSectionWrapper>
                            {renderSectionDivider(index)}
                        </Box>
                    );
                }

                // Default: render all fields in flexbox grid (matching builder layout)
                return (
                    <Box key={section.id} data-pdf-block="section">
                        <ConsentSectionWrapper section={section} theme={theme}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {visibleFields.map((field) => (
                                    <Box key={field.id} data-pdf-block="field" sx={{ flex: getFieldWidth(field), minWidth: 0 }}>
                                        <ConsentFieldRenderer
                                            field={field}
                                            value={fieldValues[field.id]}
                                            onChange={onChange}
                                            error={errors[field.id]}
                                            readOnly={readOnly}
                                            theme={theme}
                                            onBlur={onFieldBlur}
                                            forPdf={forPdf}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </ConsentSectionWrapper>
                        {renderSectionDivider(index)}
                    </Box>
                );
            })}
        </Box>
    );
};

ConsentFormRenderer.propTypes = {
    sections: PropTypes.array.isRequired,
    fieldValues: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    errors: PropTypes.object,
    conditionalLogics: PropTypes.array,
    leadData: PropTypes.object,
    brandInfo: PropTypes.shape({
        logoUrl: PropTypes.string,
        practiceName: PropTypes.string,
        primaryColor: PropTypes.string,
    }),
    readOnly: PropTypes.bool,
    theme: PropTypes.object,
    onFieldBlur: PropTypes.func,
    sectionIds: PropTypes.arrayOf(PropTypes.string),
    skipBrandSection: PropTypes.bool,
    // When true, every nested field/section renders in its "fully expanded"
    // presentation. Used by the off-screen PDF snapshot in AdvancedFormFlow
    // so html2canvas captures the full helper text rather than the collapsed
    // "Learn more…" summary.
    forPdf: PropTypes.bool,
};

ConsentFormRenderer.defaultProps = {
    errors: {},
    conditionalLogics: [],
    leadData: null,
    brandInfo: null,
    readOnly: false,
    theme: null,
    onFieldBlur: null,
    sectionIds: null,
    skipBrandSection: false,
    forPdf: false,
};

export default ConsentFormRenderer;
